import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  GetVaccinationForVerification_getVaccinationForVerification_pagination,
  GetVaccinationForVerification_getVaccinationForVerification_vaccinations,
  getFormattedDateAndTime,
  useDenyVaccination,
} from '@modmd/data'
import { DEVICE, COLOR } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { useToaster } from 'utils/useToaster'
import { AddStaffVerifier } from 'internal-portal/routes/VaccineRecordVerification/AddStaffVerifier'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  result?: string | null
  testCategory?: string | null
  testSessionName?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  data: GetVaccinationForVerification_getVaccinationForVerification_vaccinations[]
  refetchData?: () => void
  pagination?: GetVaccinationForVerification_getVaccinationForVerification_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (
    data: GetVaccinationForVerification_getVaccinationForVerification_vaccinations
  ) => void
}

const defaultColumnLabels = {
  name: 'Name',
  birthDate: 'Birthdate',
  actions: 'Actions',
  ignore: 'Ignore',
}

const PAGE_LENGTH = 10

export const VaccineRecordVerificationDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  data,
  refetchData,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(
    () =>
      isNull(columnLabels.ignore)
        ? `repeat(${numOfColumns}, 1fr)`
        : `repeat(${numOfColumns - 1}, 1fr) 5rem`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numOfColumns]
  )
  const { setToastMessage } = useToaster()
  const [denyVaccination] = useDenyVaccination()

  const handleDenyVaccination = async (userId: string) => {
    await denyVaccination({
      variables: {
        inputData: {
          userId,
          observation: 'Ignored',
        },
      },
    })
    setToastMessage('Vaccination ignored', 'success')
    if (refetchData) refetchData()
  }

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={data}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.name) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.birthDate) ? (
                <Datalist.HeaderCell label={columnLabels.birthDate} />
              ) : null}
              {!isNull(columnLabels.actions) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
              {!isNull(columnLabels.ignore) ? <Datalist.HeaderCell label="Ignore" /> : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { User } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {User && (
                      <>
                        {User.firstName} {User.lastName}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.birthDate) ? (
                  <Datalist.Cell label={columnLabels.birthDate}>
                    {getFormattedDateAndTime(User.birthDate)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell label={columnLabels.actions}>
                    <AddStaffVerifier vaccine id={data.id} user={data.userId} />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.ignore) ? (
                  <Datalist.Cell label={columnLabels.ignore} width="16px">
                    <Button appearance="link" onClick={() => handleDenyVaccination(User.id)}>
                      <Icon.Close size="XL" color={COLOR.danger} />
                    </Button>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
